import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, Users, Clock, Calendar, FileText, Coins, Wallet,
  BadgeDollarSign, Award, ShieldCheck, FolderKanban, ClipboardList, Building2,
  Sparkles, MapPin, Mail, Phone, ChevronDown, FileBarChart2, UserCog, Send, MessageSquare, User,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const MODULOS = [
  { icon: Users, title: "Empleados", desc: "Expedientes digitales completos con datos personales, laborales, bancarios, contactos de emergencia y documentos adjuntos." },
  { icon: Clock, title: "Asistencia", desc: "Registro diario de entrada/salida con detección automática de jornada (diurna, mixta, nocturna) y horas extras." },
  { icon: Calendar, title: "Vacaciones", desc: "Cálculo automático del saldo (1 día por mes trabajado) con historial de solicitudes, aprobaciones y disfrutes." },
  { icon: FileText, title: "Planillas", desc: "Semanal, quincenal y mensual con cálculo automático de CCSS (10.83% / 5.5% pensionado), Art. 138, Art. 143 y feriados." },
  { icon: Coins, title: "Aguinaldo", desc: "Cálculo automático en vivo desde asistencia (dic → nov) según Ley 2412 y su reglamento. Comprobante PDF listo para pagar." },
  { icon: Wallet, title: "Rebajos", desc: "Multi-concepto: adelantos, alimentación, embargo, pensión alimentaria, uniforme, caja de ahorro, asociación solidarista y otros." },
  { icon: BadgeDollarSign, title: "Bonificaciones", desc: "Bonos regulares o excepcionales con opción CCSS/no CCSS y aplicación selectiva por período." },
  { icon: Award, title: "Liquidaciones", desc: "Preaviso + cesantía + aguinaldo proporcional + vacaciones proporcionales calculados con salarios reales de los últimos 12 meses." },
  { icon: ShieldCheck, title: "Incapacidades", desc: "Registro de incapacidades CCSS/INS con reglas Art. 79/80 y afectación automática de días pagos y no pagos." },
  { icon: FolderKanban, title: "Documentación", desc: "Editor enriquecido para actas, cartas, ascensos, despidos y acciones de personal. Utilizando plantillas reutilizables y editables." },
  { icon: ClipboardList, title: "Solicitudes", desc: "Portal para que empleados envíen solicitudes de vacaciones/incapacidad y RRHH las apruebe con audit log." },
  { icon: Building2, title: "Contratos", desc: "Adjuntá el contrato firmado en PDF (hasta 20 MB) por empleado. Consultá, reemplazá o eliminá desde una vista central." },
  { icon: FileBarChart2, title: "Reportes", desc: "9 tipos de reportes: semanales, mensuales, por empleado, con o sin CCSS, filtros avanzados. Exportables a PDF y Excel." },
  { icon: UserCog, title: "Acciones de Personal", desc: "Ascensos, descensos, renuncias, despidos, cambios de puesto, amonestaciones y período de prueba con seguimiento automático." },
];

const BENEFICIOS = [
  { icon: MapPin, title: "Hecho para Costa Rica", desc: "Cumplimiento riguroso del Código de Trabajo, Ley del Aguinaldo y normativa CCSS. Cálculos revisados por profesionales del área." },
  { icon: Sparkles, title: "Interfaz elegante y rápida", desc: "Diseñado para que la persona que gestiona nómina disfrute el trabajo diario. Sin páginas lentas ni menús confusos." },
  { icon: ShieldCheck, title: "Seguridad y auditoría", desc: "Roles (Administrador · Asistente), audit log de acciones críticas y encriptación de sesiones. Tus datos protegidos." },
  { icon: FileText, title: "Reportes y comprobantes", desc: "Boletas de pago PDF, reportes Excel con estilos y envío por correo integrado." },
];

// Planes SaaS de VérticePro. Todos los precios en colones (CRC), IVA incluido.
// `base` = monto fijo mensual, `porColab` = costo adicional por colaborador.
export const PLANES = [
  { key: "plata",    nombre: "Plata",    rango: "1 a 10 colaboradores",     base: 10000,  porColab: 3000, features: ["Todos los módulos", "Reportes PDF / Excel", "Cálculo automático CCSS", "Comprobantes por correo", "Soporte por correo"] },
  { key: "oro",      nombre: "Oro",      rango: "11 a 50 colaboradores",    base: 13000,  porColab: 2700, features: ["Todos los módulos", "Reportes PDF / Excel", "Cálculo automático CCSS", "Comprobantes por correo", "Soporte prioritario"] },
  { key: "platino",  nombre: "Platino",  rango: "51 a 100 colaboradores",   base: 23000,  porColab: 2500, features: ["Todos los módulos", "Reportes PDF / Excel", "Cálculo automático CCSS", "Comprobantes por correo", "Onboarding personalizado"] },
  { key: "diamante", nombre: "Diamante", rango: "101 a 250 colaboradores",  base: 53000,  porColab: 2200, features: ["Todos los módulos", "Reportes PDF / Excel", "Cálculo automático CCSS", "Comprobantes por correo", "Soporte dedicado + SLA"] },
  { key: "master", nombre: "Master", rango: "250+ colaboradores", base: 113000, porColab: 2000, features: ["Todos los módulos", "Reportes PDF / Excel", "Cálculo automático CCSS", "Comprobantes por correo", "Gerente de cuenta asignado + SLA premium"] },
];
// Frecuencias de pago disponibles. `meses` sirve para calcular el próximo pago.
export const FRECUENCIAS_PAGO = [
  { key: "mensual",     label: "Mensual",    meses: 1,  descuento: 0    },
  { key: "trimestral",  label: "Trimestral", meses: 3,  descuento: 0.10 },
  { key: "semestral",   label: "Semestral",  meses: 6,  descuento: 0.15 },
  { key: "anual",       label: "Anual",      meses: 12, descuento: 0.20 },
];

const FAQS = [
  { q: "¿Qué puedo administrar en el sistema?", a: "Empleados (expedientes completos), asistencia diaria, incapacidades CCSS/INS, vacaciones, rebajos multi-concepto, bonificaciones, planillas semanales/quincenales/mensuales, aguinaldo, liquidaciones, contratos PDF, documentación y acciones de personal (ascensos, despidos, amonestaciones)." },
  { q: "¿Cumple con la legislación costarricense?", a: "Sí. Se aplican reglas del Código de Trabajo (Art. 138 día libre, Art. 143 jefaturas sin extras, Art. 79/80 incapacidades), Ley del Aguinaldo (dic-nov ÷ 12) y CCSS (10.83% general o 5.5% pensionado)." },
  { q: "¿Se pueden exportar reportes?", a: "Sí. Toda planilla se exporta a Excel con estilos, PDF individual por empleado (boleta) y adicionalmente un Reporte de Pago compacto (PDF y Excel) para agilizar transferencias bancarias." },
  { q: "¿Puedo tener varios usuarios?", a: "Sí. Soporta múltiples perfiles con dos roles: Administrador (acceso total) y Asistente (acceso limitado a operación diaria). Todas las acciones sensibles quedan registradas en el audit log." },
  { q: "¿Hay demo del sistema?", a: "Al registrarte tenés 25 días de uso completo del sistema, totalmente gratis y sin restricciones — podés probar todos los módulos con tus propios datos. Al finalizar el período de prueba, el sistema te consultará si querés continuar con un plan de suscripción y podrás subir tu comprobante de pago desde tu cuenta para reactivar tu acceso." },
  { q: "¿Cuándo tengo que realizar mi pago?", a: "El cobro se genera el día 25 de cada ciclo y tenés hasta 8 días naturales para acreditar el pago (por SINPE Móvil o transferencia). El ciclo depende de la frecuencia que hayas elegido al registrarte: Mensual (todos los meses), Trimestral (cada 3 meses), Semestral (cada 6 meses) o Anual (una vez al año). Elegir un ciclo más largo te da descuentos: 10% trimestral, 15% semestral, 20% anual." },
  { q: "¿Puedo solicitar una reunión virtual para conocer el sistema?", a: "¡Claro! Desde la sección Contáctanos podés escribirnos indicando en el motivo \"Quiero una demo\" y coordinamos una videollamada gratuita en la que te acompañamos paso a paso por cada módulo, resolvemos tus dudas y te mostramos cómo se adaptaría el sistema a la operación de tu empresa." },
];

export default function Landing() {
  const { user } = useAuth();
  const [faq, setFaq] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-[#1A2421]" data-testid="landing-root">
      {/* NAV */}
      <nav className={`fixed top-0 inset-x-0 z-50 transition-all ${scrolled ? "bg-white/95 backdrop-blur border-b border-c shadow-sm" : "bg-transparent"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-2.5">
            <img src="/vertice-logo.png" alt="Vértice" className="h-9 w-auto" />
          </a>
          <div className="hidden md:flex items-center gap-7 text-sm font-medium">
            <a href="#modulos" className="hover:text-[#274735]" data-testid="nav-modulos">Módulos</a>
            <a href="#beneficios" className="hover:text-[#274735]" data-testid="nav-beneficios">Beneficios</a>
            <a href="#precios" className="hover:text-[#274735]" data-testid="nav-precios">Precios</a>
            <a href="#faq" className="hover:text-[#274735]" data-testid="nav-faq">FAQ</a>
            <a href="#contacto" className="hover:text-[#274735]" data-testid="nav-contacto">Contáctanos</a>
          </div>
          <div className="flex items-center gap-2">
            {user ? (
              <Link to="/dashboard" className="np-btn np-btn-primary inline-flex items-center gap-1.5" data-testid="cta-dashboard">
                Vamos <ArrowRight size={14} />
              </Link>
            ) : (
              <>
                <Link to="/login" className="np-btn np-btn-outline hidden sm:inline-flex" data-testid="cta-login">Iniciar sesión</Link>
                <Link to="/register" className="np-btn np-btn-primary inline-flex items-center gap-1.5" data-testid="cta-register">
                  Crear cuenta <ArrowRight size={14} />
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section id="top" className="pt-32 pb-20 md:pt-40 md:pb-28 relative overflow-hidden">
        {/* Decorative shapes */}
        <div className="absolute -top-10 -right-20 w-96 h-96 rounded-full bg-[#D96C4A] opacity-10 blur-3xl" />
        <div className="absolute top-40 -left-20 w-80 h-80 rounded-full bg-[#274735] opacity-10 blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E6EFEA] text-[#274735] text-xs font-medium mb-6">
                <Sparkles size={13} /> Sistema de nómina y RRHH para Costa Rica
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight mb-6">
                Planillas que se <span className="text-[#274735]">calculan solas.</span>
              </h1>
              <p className="text-lg text-secondary-c leading-relaxed mb-6 max-w-2xl">
                Gestión integral de personal, asistencia y nómina para empresas costarricenses. Cálculos automáticos con CCSS, horas extra y rebajos.
              </p>
              <ul className="grid sm:grid-cols-2 gap-y-2 gap-x-6 mb-8 max-w-2xl" data-testid="hero-bullets">
                {[
                  "Control de asistencia diario",
                  "Cálculo de planillas semanales / mensuales",
                  "Deducciones CCSS automáticas (10.83%)",
                  "Gestión integral de empleados",
                ].map(item => (
                  <li key={item} className="flex items-center gap-2 text-sm text-secondary-c">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#D96C4A] flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap items-center gap-3">
                <Link to={user ? "/dashboard" : "/register"} className="np-btn np-btn-primary inline-flex items-center gap-1.5 text-base py-3 px-6" data-testid="hero-cta-primary">
                  {user ? "Vamos" : "Comenzar ahora"} <ArrowRight size={16} />
                </Link>
                <a href="#modulos" className="np-btn np-btn-outline text-base py-3 px-6" data-testid="hero-cta-modulos">Ver módulos</a>
              </div>
            </div>
            <div className="lg:col-span-5 hidden lg:block">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#274735]/10 to-[#D96C4A]/10 rounded-3xl blur-2xl" />
                <div className="relative bg-white rounded-2xl p-6 shadow-xl border border-c">
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-xs font-semibold text-secondary-c uppercase tracking-wide">Planilla · Semana actual</div>
                    <div className="inline-flex items-center gap-1 text-xs text-[#274735] font-medium"><CheckCircle2 size={12} /> Al día</div>
                  </div>
                  <div className="space-y-3">
                    {[
                      { n: "María Rojas", bruto: "₡ 342.850", ccss: "₡ 37.131" },
                      { n: "Carlos Vega", bruto: "₡ 258.400", ccss: "₡ 27.985" },
                      { n: "Andrea Solano", bruto: "₡ 415.200", ccss: "₡ 44.966" },
                    ].map(r => (
                      <div key={r.n} className="flex items-center justify-between p-3 rounded-xl bg-[#FAFAF7]">
                        <div className="text-sm font-medium">{r.n}</div>
                        <div className="text-right">
                          <div className="text-sm font-semibold">{r.bruto}</div>
                          <div className="text-[11px] text-error">−{r.ccss}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-3 border-t border-c flex justify-between text-sm">
                    <span className="text-secondary-c">Total neto:</span>
                    <span className="font-display font-semibold text-lg text-[#274735]">₡ 906.368</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFICIOS */}
      <section id="beneficios" className="py-20 bg-white border-y border-c">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="text-xs font-semibold text-[#D96C4A] uppercase tracking-widest mb-3">Por qué elegirnos</div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold mb-3">Menos error humano, más tiempo para tu equipo</h2>
            <p className="text-secondary-c max-w-2xl mx-auto">Diseñado desde cero para empresas costarricenses que valoran precisión, cumplimiento y una experiencia limpia.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {BENEFICIOS.map((b) => (
              <div key={b.title} className="p-6 rounded-2xl border border-c bg-[#FAFAF7] hover:bg-white hover:shadow-md transition-all">
                <div className="w-11 h-11 rounded-xl bg-[#E6EFEA] text-[#274735] flex items-center justify-center mb-4">
                  <b.icon size={20} strokeWidth={1.6} />
                </div>
                <div className="font-display text-lg font-semibold mb-1.5">{b.title}</div>
                <p className="text-sm text-secondary-c leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MODULOS */}
      <section id="modulos" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="text-xs font-semibold text-[#D96C4A] uppercase tracking-widest mb-3">Todo en un solo lugar</div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold mb-3">14 módulos que cubren todo el ciclo laboral</h2>
            <p className="text-secondary-c max-w-2xl mx-auto">Desde el ingreso del empleado hasta la liquidación final, sin necesidad de hojas paralelas ni servicios externos.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {MODULOS.map((m, i) => (
              <div key={m.title} className="p-6 rounded-2xl bg-white border border-c hover:border-[#274735] hover:-translate-y-0.5 transition-all">
                <div className="flex items-start gap-4">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${i % 3 === 0 ? "bg-[#E6EFEA] text-[#274735]" : i % 3 === 1 ? "bg-[#FBEDE6] text-[#D96C4A]" : "bg-[#F5EFE5] text-[#7A6547]"}`}>
                    <m.icon size={20} strokeWidth={1.6} />
                  </div>
                  <div className="flex-1">
                    <div className="font-display text-lg font-semibold mb-1.5">{m.title}</div>
                    <p className="text-sm text-secondary-c leading-relaxed">{m.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRECIOS */}
      <section id="precios" className="py-20 bg-[#F5EFE5]/30 border-y border-c">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="text-xs font-semibold text-[#D96C4A] uppercase tracking-widest mb-3">Precios</div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold mb-3">Planes que crecen con tu empresa</h2>
            <p className="text-secondary-c max-w-2xl mx-auto">Todos los planes incluyen los 14 módulos completos. IVA incluido. Elegí ciclo de pago con descuento (hasta 20% anual).</p>
          </div>
          {/* 3 planes arriba + 2 abajo centrados (para que Plan Master no quede solo) */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-6">
            {PLANES.slice(0, 3).map(p => <PlanCard key={p.nombre} p={p} user={user} />)}
          </div>
          <div className="flex flex-wrap justify-center gap-5 mb-10">
            {PLANES.slice(3).map(p => (
              <div key={p.nombre} className="w-full md:w-[calc(50%-10px)] lg:w-[calc(33.333%-14px)]">
                <PlanCard p={p} user={user} />
              </div>
            ))}
          </div>

          {/* Descuentos */}
          <div className="max-w-3xl mx-auto bg-white rounded-2xl p-6 md:p-8 border border-c shadow-sm">
            <div className="text-center mb-6">
              <h3 className="font-display text-xl font-semibold mb-1">Descuentos por pago anticipado</h3>
              <p className="text-sm text-secondary-c">Menor costo por colaborador · Gestión más predecible</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { m: "Trimestral", d: "10%" },
                { m: "Semestral", d: "15%" },
                { m: "Anual", d: "20%" },
              ].map(x => (
                <div key={x.m} className="text-center p-4 rounded-xl bg-[#FAFAF7] border border-c">
                  <div className="text-xs text-secondary-c uppercase tracking-wider mb-1">{x.m}</div>
                  <div className="font-display text-2xl font-semibold text-[#D96C4A]">{x.d}</div>
                  <div className="text-xs text-secondary-c mt-0.5">de descuento</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="text-xs font-semibold text-[#D96C4A] uppercase tracking-widest mb-3">Preguntas frecuentes</div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold">Todo lo que querés saber</h2>
          </div>
          <div className="space-y-3">
            {FAQS.map((f, i) => {
              const open = faq === i;
              return (
                <div key={f.q} className="bg-white rounded-2xl border border-c overflow-hidden">
                  <button onClick={() => setFaq(open ? null : i)} className="w-full p-5 text-left flex items-center justify-between gap-4 hover:bg-[#FAFAF7]" data-testid={`faq-toggle-${i}`}>
                    <span className="font-medium">{f.q}</span>
                    <ChevronDown size={18} className={`text-secondary-c transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open && (
                    <div className="px-5 pb-5 pt-0 text-sm text-secondary-c leading-relaxed">{f.a}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CONTÁCTANOS */}
      <section id="contacto" className="py-20 bg-[#FAFAF7]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="text-xs font-semibold text-[#D96C4A] uppercase tracking-widest mb-3">Contáctanos</div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold mb-3">¿Tenés dudas? Escribinos.</h2>
            <p className="text-secondary-c">Contanos qué necesitás y te respondemos personalizado — sin bots, sin filas.</p>
          </div>
          <ContactoForm />
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-20 bg-[#274735] relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-[#D96C4A] opacity-20 blur-3xl" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white relative">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold mb-4">
            Empezá hoy a gestionar tu planilla como debe ser.
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            Sin compromisos largos. Con soporte real. Diseñado para Costa Rica.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {user ? (
              <Link to="/dashboard" className="np-btn bg-white text-[#274735] hover:bg-[#FAFAF7] inline-flex items-center gap-1.5 text-base py-3 px-6" data-testid="cta-final-dashboard">
                Vamos <ArrowRight size={16} />
              </Link>
            ) : (
              <>
                <Link to="/register" className="np-btn bg-white text-[#274735] hover:bg-[#FAFAF7] inline-flex items-center gap-1.5 text-base py-3 px-6" data-testid="cta-final-register">
                  Crear cuenta gratis <ArrowRight size={16} />
                </Link>
                <Link to="/login" className="np-btn bg-transparent border-2 border-white/40 text-white hover:bg-white/10 text-base py-3 px-6" data-testid="cta-final-login">
                  Ya tengo cuenta
                </Link>
              </>
            )}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-10 border-t border-c bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2.5 mb-3">
                <img src="/vertice-logo.png" alt="Vértice" className="h-8 w-auto" />
              </div>
              <p className="text-sm text-secondary-c">Sistema de gestión de nómina y recursos humanos para Costa Rica. Precisión, cumplimiento y experiencia limpia.</p>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-secondary-c mb-3">Producto</div>
              <ul className="space-y-2 text-sm">
                <li><a href="#modulos" className="hover:text-[#274735]">Módulos</a></li>
                <li><a href="#beneficios" className="hover:text-[#274735]">Beneficios</a></li>
                <li><a href="#precios" className="hover:text-[#274735]">Precios</a></li>
                <li><a href="#faq" className="hover:text-[#274735]">FAQ</a></li>
                <li><a href="#contacto" className="hover:text-[#274735]">Contáctanos</a></li>
              </ul>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-secondary-c mb-3">Contacto</div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2"><Mail size={14} className="text-secondary-c" /> grupoverticecr@gmail.com</li>
                <li className="flex items-center gap-2"><Phone size={14} className="text-secondary-c" /> +506 8596-7408</li>
                <li className="flex items-center gap-2"><MapPin size={14} className="text-secondary-c" /> Esparza, Puntarenas, Costa Rica</li>
              </ul>
            </div>
          </div>
          <div className="pt-6 border-t border-c flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-secondary-c">
            <div>© {new Date().getFullYear()} Grupo Vértice CR. Todos los derechos reservados.</div>
            <div>Hecho en Costa Rica</div>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ==================== Card individual de un plan ====================
function PlanCard({ p, user }) {
  return (
    <div className={`p-6 rounded-2xl border-2 transition-all ${p.destacado ? "border-[#274735] bg-white shadow-xl scale-[1.02]" : "border-c bg-white hover:shadow-md"}`}>
      {p.destacado && (
        <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#274735] text-white text-[10px] font-semibold uppercase tracking-wider mb-3">
          Más popular
        </div>
      )}
      <div className="font-display text-xl font-semibold mb-1">Plan {p.nombre}</div>
      <div className="text-xs text-secondary-c mb-4">{p.rango}</div>
      <div className="flex items-baseline gap-1 mb-1">
        <span className="font-display text-3xl font-semibold text-[#274735]">₡{p.base.toLocaleString("es-CR")}</span>
        <span className="text-xs text-secondary-c">/ mes base</span>
      </div>
      <div className="text-sm font-medium text-[#274735] mb-1">+ ₡{p.porColab.toLocaleString("es-CR")} por colaborador</div>
      <div className="text-[10px] text-secondary-c uppercase tracking-wider mb-5">IVA incluido</div>
      <ul className="space-y-2 mb-6">
        {p.features.map(f => (
          <li key={f} className="flex items-start gap-2 text-sm">
            <CheckCircle2 size={15} className="text-[#274735] mt-0.5 flex-shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Link
        to={user ? "/dashboard" : `/register?plan=${p.key}`}
        className={`np-btn w-full justify-center ${p.destacado ? "np-btn-primary" : "np-btn-outline"}`}
        data-testid={`plan-cta-${p.key}`}
      >
        {user ? "Vamos" : "Comenzar"}
      </Link>
    </div>
  );
}

// ==================== Formulario de contacto ====================
function ContactoForm() {
  const [form, setForm] = useState({ nombre: "", correo: "", telefono: "", motivo: "informacion", mensaje: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.correo || !form.mensaje) {
      toast.error("Completá nombre, correo y mensaje.");
      return;
    }
    setSaving(true);
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${base}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "No se pudo enviar el mensaje");
      }
      toast.success("¡Gracias! Recibimos tu mensaje. Te contactamos pronto.");
      setForm({ nombre: "", correo: "", telefono: "", motivo: "informacion", mensaje: "" });
    } catch (err) {
      toast.error(err.message || "Ocurrió un problema al enviar. Escribinos a grupoverticecr@gmail.com");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl border border-c shadow-sm p-6 md:p-8 space-y-4" data-testid="contacto-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="np-label"><User size={12} className="inline mr-1" /> Nombre completo *</label>
          <input className="np-input" value={form.nombre} onChange={e => set("nombre", e.target.value)} required data-testid="contacto-nombre" placeholder="Ej: María Rojas Vargas" />
        </div>
        <div>
          <label className="np-label"><Mail size={12} className="inline mr-1" /> Correo *</label>
          <input type="email" className="np-input" value={form.correo} onChange={e => set("correo", e.target.value)} required data-testid="contacto-correo" placeholder="tu@correo.com" />
        </div>
        <div>
          <label className="np-label"><Phone size={12} className="inline mr-1" /> Teléfono</label>
          <input type="tel" className="np-input" value={form.telefono} onChange={e => set("telefono", e.target.value)} data-testid="contacto-telefono" placeholder="8888-8888" />
        </div>
        <div>
          <label className="np-label"><ClipboardList size={12} className="inline mr-1" /> Motivo</label>
          <select className="np-input" value={form.motivo} onChange={e => set("motivo", e.target.value)} data-testid="contacto-motivo">
            <option value="informacion">Solicito información</option>
            <option value="demo">Quiero una demo</option>
            <option value="ventas">Consulta comercial / precios</option>
            <option value="soporte">Soporte técnico</option>
            <option value="otro">Otro</option>
          </select>
        </div>
      </div>
      <div>
        <label className="np-label"><MessageSquare size={12} className="inline mr-1" /> Mensaje *</label>
        <textarea className="np-input min-h-[120px]" value={form.mensaje} onChange={e => set("mensaje", e.target.value)} required data-testid="contacto-mensaje" placeholder="Contanos brevemente qué necesitás…" />
      </div>
      <div className="flex justify-end">
        <button type="submit" disabled={saving} className="np-btn np-btn-primary disabled:opacity-60" data-testid="contacto-enviar">
          <Send size={14} /> {saving ? "Enviando…" : "Enviar mensaje"}
        </button>
      </div>
    </form>
  );
}
